import React from 'react';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { Toaster } from 'react-hot-toast';
import {
  Helmet,
  HelmetProvider,
} from 'react-helmet-async';

import {
  AuthProvider,
  useAuth,
} from './context/AuthContext';

import { SocketProvider } from './context/SocketContext';
import { PWAInstallProvider } from './context/PWAInstallContext';
import PWAInstallGate from './components/PWAInstallGate';
import Layout from './components/Layout';

// Public pages
import ApplySchool from './pages/public/ApplySchool';
import LandingPage from './pages/public/LandingPage';
import OnboardingSuccess from './pages/public/OnboardingSuccess';
import Subscribe from './pages/public/Subscribe';
import Checkout from './pages/public/Checkout';
import Login from './pages/public/Login';
import SuperAdminRegister from './pages/super-admin/SuperAdminRegister';
// SuperAdminLogin is no longer used – removed import
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import SuperAdminSchools from './pages/super-admin/SuperAdminSchools';
import SuperAdminSubscriptions from './pages/super-admin/SuperAdminSubscriptions';
import SuperAdminFinance from './pages/super-admin/SuperAdminFinance';
import SuperAdminAcademicCalendar from './pages/super-admin/SuperAdminAcademicCalendar';
import SuperAdminProfile from './pages/super-admin/SuperAdminProfile';
import SuperAdminSettings from './pages/super-admin/SuperAdminSettings';
import SuperAdminLayout from './components/super-admin/SuperAdminLayout';

// Main application pages
import Dashboard from './pages/Dashboard';

import Students from './pages/Students';
import AddStudent from './pages/AddStudent';
import EditStudent from './pages/EditStudent';
import StudentDetails from './pages/StudentDetails';
import StudentGradeSheet from './pages/StudentGradeSheet';
import StudentAttendance from './pages/attendance/StudentAttendance';
import StudentGradeEditor from './pages/StudentGradeEditor';
import StudentProfile from './pages/profiles/StudentProfile';

import Teachers from './pages/Teachers';
import AddTeacher from './pages/AddTeacher';
import EditTeacher from './pages/EditTeacher';
import TeacherDetails from './pages/TeacherDetails';

import Classes from './pages/Classes';
import AddClass from './pages/AddClass';
import EditClass from './pages/EditClass';
import ClassDetails from './pages/ClassDetails';
import MarkAttendance from './pages/MarkAttendance';
import AttendanceRegister from './pages/AttendanceRegister';

import Subjects from './pages/Subjects';
import StudentSubjects from './pages/subjects/StudentSubjects';
import Library from './pages/library/Library';

// Role-aware notifications and student-specific pages
import StudentNotifications from './pages/notifications/Notifications';
import StudentGrades from './pages/grades/StudentGrades';
import StudentTimetable from './pages/timetable/StudentTimetable';
import MasterTimetable from './pages/timetable/MasterTimetable';

// Parent-specific pages
import ParentGrades from './pages/grades/ParentGrades';

// School finance pages
import FinanceDashboard from './pages/finance/FinanceDashboard';
import FeeSetup from './pages/finance/FeeSetup';
import RecordPayment from './pages/finance/RecordPayment';
import PaymentHistory from './pages/finance/PaymentHistory';
import StudentFeeDetails from './pages/finance/StudentFeeDetails';

import Settings from './pages/Settings';
import NotFound from './pages/NotFound';

import { PageLoader } from './components/Loader';
import PricingModal from './components/PricingModal';

import './index.css';

/* =========================================================
   AUTH-ONLY ROUTE

   Allows logged-in users to access:
   - Subscription page
   - Pricing page
   - Checkout page

   A paid plan is not required for these pages.
========================================================= */
const AuthOnlyRoute = ({ children }) => {
  const {
    isAuthenticated,
    loading,
    authReady,
  } = useAuth();

  const location = useLocation();

  if (loading || !authReady) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    const returnUrl =
      location.pathname + location.search;

    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          returnUrl
        )}`}
        replace
      />
    );
  }

  return children;
};

/* =========================================================
   PROTECTED ROUTE

   Requires:
   - Authenticated user
   - Active paid subscription

   Trial, inactive, suspended and expired plans are blocked.
========================================================= */
const ProtectedRoute = ({ children }) => {
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    hasPaidPlan,
  } = useAuth();

  const location = useLocation();

  if (loading || !authReady) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    const returnUrl =
      location.pathname + location.search;

    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          returnUrl
        )}`}
        replace
      />
    );
  }

  if (String(user?.role || '').toLowerCase() === 'super_admin') {
    return (
      <Navigate
        to="/super-admin/dashboard"
        replace
      />
    );
  }

  if (!hasPaidPlan) {
    return (
      <Navigate
        to="/subscribe_to_plan"
        replace
      />
    );
  }

  return children;
};

/* =========================================================
   ROLE-BASED PROTECTION
========================================================= */
const RoleProtectedRoute = ({
  children,
  allowedRoles,
}) => {
  const {
    user,
    loading,
    authReady,
  } = useAuth();

  const location = useLocation();

  if (loading || !authReady) {
    return <PageLoader />;
  }

  if (!user) {
    const returnUrl =
      location.pathname + location.search;

    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          returnUrl
        )}`}
        replace
      />
    );
  }

  const normalizedRole = String(
    user.role || ''
  ).toLowerCase();

  if (
    !allowedRoles
      .map((role) => role.toLowerCase())
      .includes(normalizedRole)
  ) {
    return (
      <Navigate
        to="/app/dashboard"
        replace
      />
    );
  }

  return children;
};

/* =========================================================
   PLAN FEATURE PROTECTION
   readOnly=true allows the Free plan to view existing core data, while
   create/edit/delete pages still require the actual plan feature.
========================================================= */
const PlanFeatureRoute = ({ children, feature, readOnly = false }) => {
  const {
    hasFeature,
    canViewFeature,
    planAccessLoading,
  } = useAuth();
  const location = useLocation();

  if (planAccessLoading) return <PageLoader />;

  const allowed = readOnly
    ? canViewFeature(feature)
    : hasFeature(feature);

  if (!allowed) {
    return (
      <Navigate
        to={`/app/pricing?feature=${encodeURIComponent(feature)}&from=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  return children;
};

/* =========================================================
   SUPER ADMIN ROUTE

   Separate from school subscription and tenant protection.
   Now redirects to /login (instead of /super-admin/login)
========================================================= */
const SuperAdminRoute = ({ children }) => {
  const { user, loading, authReady } = useAuth();
  const location = useLocation();

  if (loading || !authReady) {
    return <PageLoader />;
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`}
        replace
      />
    );
  }

  if (String(user.role || '').toLowerCase() !== 'super_admin') {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

/* =========================================================
   SUBJECTS COMPONENT SELECTOR
========================================================= */
const SubjectsRouter = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  if (role === 'student') {
    return <StudentSubjects />;
  }

  return <Subjects />;
};

/* =========================================================
   TIMETABLE COMPONENT SELECTOR

   Admins and teachers use the master timetable.
   Students see only their assigned class timetable.
========================================================= */
const TimetableRouter = () => {
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();

  if (role === 'student') {
    return <StudentTimetable />;
  }

  return <MasterTimetable />;
};

/* =========================================================
   PRICING PAGE WRAPPER
========================================================= */
const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <PricingModal
      isOpen={true}
      onClose={() =>
        navigate('/subscribe_to_plan', {
          replace: true,
        })
      }
    />
  );
};

/* =========================================================
   APP ROUTES
========================================================= */
function AppRoutes() {
  return (
    <Routes>
      {/* ================= PUBLIC ROUTES ================= */}

      <Route
        path="/"
        element={<LandingPage />}
      />

      <Route
        path="/apply"
        element={<ApplySchool />}
      />

      <Route
        path="/onboarding/success"
        element={<OnboardingSuccess />}
      />

      <Route
        path="/login"
        element={<Login />}
      />

      <Route
        path="/super-admin/register"
        element={<SuperAdminRegister />}
      />

      {/* SuperAdminLogin route removed */}

      <Route
        path="/super-admin"
        element={<Navigate to="/super-admin/dashboard" replace />}
      />

      <Route
        path="/super-admin/dashboard"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminDashboard />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/schools"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminSchools />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/subscriptions"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminSubscriptions />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/finance"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminFinance />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/academic-calendar"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminAcademicCalendar />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/profile"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminProfile />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/super-admin/settings"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout>
              <SuperAdminSettings />
            </SuperAdminLayout>
          </SuperAdminRoute>
        }
      />

      {/* Old subscription URL */}
      <Route
        path="/subscribe"
        element={
          <Navigate
            to="/subscribe_to_plan"
            replace
          />
        }
      />

      {/* ================= SUBSCRIPTION BRIDGE ================= */}

      {/* Logged in, but a paid plan is not required */}
      <Route
        path="/subscribe_to_plan"
        element={
          <AuthOnlyRoute>
            <Subscribe />
          </AuthOnlyRoute>
        }
      />

      <Route
        path="/app/pricing"
        element={
          <AuthOnlyRoute>
            <PricingPage />
          </AuthOnlyRoute>
        }
      />

      <Route
        path="/app/checkout"
        element={
          <AuthOnlyRoute>
            <Checkout />
          </AuthOnlyRoute>
        }
      />

      {/* ================= PAID APPLICATION ================= */}

      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Navigate
              to="dashboard"
              replace
            />
          }
        />

        <Route
          path="dashboard"
          element={<Dashboard />}
        />

        {/* ================= STUDENTS ================= */}

        <Route
          path="students"
          element={<PlanFeatureRoute feature="students" readOnly><Students /></PlanFeatureRoute>}
        />

        <Route
          path="students/add"
          element={<PlanFeatureRoute feature="students"><AddStudent /></PlanFeatureRoute>}
        />

        <Route
          path="students/edit/:id"
          element={<PlanFeatureRoute feature="students"><EditStudent /></PlanFeatureRoute>}
        />

        <Route
          path="students/:id"
          element={<PlanFeatureRoute feature="students" readOnly><StudentDetails /></PlanFeatureRoute>}
        />

        <Route
          path="students/:id/grades"
          element={<PlanFeatureRoute feature="exams_results" readOnly><StudentGradeSheet /></PlanFeatureRoute>}
        />

        <Route
          path="students/:id/grades/edit"
          element={<PlanFeatureRoute feature="exams_results"><StudentGradeEditor /></PlanFeatureRoute>}
        />

        <Route
          path="students/:id/attendance"
          element={<PlanFeatureRoute feature="attendance" readOnly><StudentAttendance /></PlanFeatureRoute>}
        />

        {/* Student's own full profile */}
        <Route
          path="profile/student"
          element={
            <RoleProtectedRoute allowedRoles={['student']}>
              <StudentProfile />
            </RoleProtectedRoute>
          }
        />

        {/* ================= TEACHERS ================= */}

        <Route
          path="teachers"
          element={<PlanFeatureRoute feature="teachers" readOnly><Teachers /></PlanFeatureRoute>}
        />

        <Route
          path="teachers/add"
          element={<PlanFeatureRoute feature="teachers"><AddTeacher /></PlanFeatureRoute>}
        />

        <Route
          path="teachers/edit/:id"
          element={<PlanFeatureRoute feature="teachers"><EditTeacher /></PlanFeatureRoute>}
        />

        <Route
          path="teachers/:id"
          element={<PlanFeatureRoute feature="teachers" readOnly><TeacherDetails /></PlanFeatureRoute>}
        />

        {/* ================= CLASSES ================= */}

        <Route
          path="classes"
          element={<PlanFeatureRoute feature="classes" readOnly><Classes /></PlanFeatureRoute>}
        />

        <Route
          path="classes/add"
          element={<PlanFeatureRoute feature="classes"><AddClass /></PlanFeatureRoute>}
        />

        <Route
          path="classes/edit/:id"
          element={<PlanFeatureRoute feature="classes"><EditClass /></PlanFeatureRoute>}
        />

        <Route
          path="classes/:id"
          element={<PlanFeatureRoute feature="classes" readOnly><ClassDetails /></PlanFeatureRoute>}
        />

        <Route
          path="classes/:id/attendance/mark"
          element={<PlanFeatureRoute feature="attendance"><MarkAttendance /></PlanFeatureRoute>}
        />

        <Route path="classes/:id/attendance" 
        element={<PlanFeatureRoute feature="attendance" readOnly><AttendanceRegister /></PlanFeatureRoute>} 
        />

        {/* ================= STUDENT & TEACHER LIBRARY ================= */}

        <Route
          path="library"
          element={
            <RoleProtectedRoute
              allowedRoles={['student', 'teacher']}
            >
              <PlanFeatureRoute feature="library"><Library /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= ROLE-BASED NOTIFICATIONS ================= */}

        <Route
          path="notifications"
          element={
            <RoleProtectedRoute
              allowedRoles={[
                'admin',
                'school_admin',
                'super_admin',
                'teacher',
                'student',
                'parent',
              ]}
            >
              <StudentNotifications />
            </RoleProtectedRoute>
          }
        />

        {/* ================= STUDENT GRADES ================= */}

        <Route
          path="grades"
          element={
            <RoleProtectedRoute
              allowedRoles={['student']}
            >
              <PlanFeatureRoute feature="exams_results" readOnly><StudentGrades /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= ROLE-BASED TIMETABLE ================= */}

        <Route
          path="timetable"
          element={
            <RoleProtectedRoute
              allowedRoles={[
                'admin',
                'super_admin',
                'teacher',
                'student',
              ]}
            >
              <PlanFeatureRoute feature="timetable" readOnly><TimetableRouter /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= STUDENT ATTENDANCE ================= */}

        <Route
          path="attendance"
          element={
            <RoleProtectedRoute
              allowedRoles={['student']}
            >
              <PlanFeatureRoute feature="attendance" readOnly><StudentAttendance /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= SUBJECTS ================= */}

        <Route
          path="subjects"
          element={
            <RoleProtectedRoute
              allowedRoles={[
                'admin',
                'super_admin',
                'student',
              ]}
            >
              <PlanFeatureRoute feature="subjects" readOnly><SubjectsRouter /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= PARENT GRADES ================= */}

        <Route
          path="child-grades"
          element={
            <RoleProtectedRoute
              allowedRoles={['parent']}
            >
              <PlanFeatureRoute feature="parent_portal" readOnly><ParentGrades /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= SCHOOL FINANCE ================= */}

        <Route
          path="finance"
          element={
            <RoleProtectedRoute allowedRoles={['admin', 'school_admin']}>
              <PlanFeatureRoute feature="finance"><FinanceDashboard /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="finance/fees"
          element={
            <RoleProtectedRoute allowedRoles={['admin', 'school_admin']}>
              <PlanFeatureRoute feature="finance"><FeeSetup /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="finance/payments/new"
          element={
            <RoleProtectedRoute allowedRoles={['admin', 'school_admin']}>
              <PlanFeatureRoute feature="finance"><RecordPayment /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="finance/payments"
          element={
            <RoleProtectedRoute allowedRoles={['admin', 'school_admin']}>
              <PlanFeatureRoute feature="finance"><PaymentHistory /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="finance/students/:studentId"
          element={
            <RoleProtectedRoute allowedRoles={['admin', 'school_admin']}>
              <PlanFeatureRoute feature="finance"><StudentFeeDetails /></PlanFeatureRoute>
            </RoleProtectedRoute>
          }
        />

        {/* ================= SETTINGS ================= */}

        <Route
          path="settings"
          element={<Settings />}
        />

        {/* Unknown /app route */}
        <Route
          path="*"
          element={<NotFound />}
        />
      </Route>

      {/* Unknown public route */}
      <Route
        path="*"
        element={<NotFound />}
      />
    </Routes>
  );
}

/* =========================================================
   MAIN APP
========================================================= */
function App() {
  return (
    <HelmetProvider>
      <Helmet
        defaultTitle="PawaSchool – School Management System"
        titleTemplate="%s | PawaSchool"
      >
        <meta
          name="description"
          content="PawaSchool – modern school management for Sierra Leone. Manage students, teachers, exams, and compliance."
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <link
          rel="canonical"
          href="https://www.pawaschool.net/"
        />

        <meta
          property="og:site_name"
          content="PawaSchool"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
      </Helmet>

      <AuthProvider>
        <SocketProvider>
          <PWAInstallProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Toaster position="top-right" />

              {/* Floating PWA install button.
                  Shows only on /login and /app/* — hidden on the landing
                  page, /apply, subscription, checkout, super-admin, etc. */}
              <PWAInstallGate />

              <AppRoutes />
            </Router>
          </PWAInstallProvider>
        </SocketProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;